import { useCallback, useEffect, useState } from "react";

import { PlayerEvents } from "../../helpers/eventHandler";
import { usePlayerStore } from "../../store/usePlayerStore";
import { PlayerEvent, PlayerEventCallback } from "../../types/common";
import { useStoreSubscribe } from "../useStoreSubscribe/useStoreSubscribe";

export const usePlayerExternalApiEvents = () => {
  const { getState, useStoreState } = usePlayerStore();
  const setExternalEvents = useStoreState.setExternalEvents();

  const [playerEvents, setPlayerEvents] = useState<PlayerEvents>();

  const {
    onError,
    onFinish,
    onMediaChanged,
    onPause,
    onPipModeChange,
    onPlay,
    onProgress,
    onRateChange,
    onSeekEnd,
    onVolumeChange,
    onWaiting
  } = useStoreSubscribe();

  const getEventPayload = useCallback(() => {
    const state = getState();
    const { dvrWindowInSeconds, title } = state.media || {};
    return {
      buffered: state.buffered,
      dvrWindowInSeconds,
      id: state.embedId,
      isAudio: state.isAudio,
      isCasting: state.isCasting,
      isDvr: !!dvrWindowInSeconds && dvrWindowInSeconds > 0,
      isLive: state.isLive,
      isPictureInPicture: state.isPictureInPicture,
      muted: state.isMuted,
      playbackRate: state.playbackRate,
      seekableEnd: state.seekableEnd || 0,
      time: Math.max(state.currentTime, 0),
      title,
      volume: state.volume
    };
  }, [getState]);

  const dispatch = useCallback(
    (event: PlayerEvent) => () => {
      const { error } = getState();
      playerEvents?.dispatch(event, getEventPayload(), error && { error });
    },
    [getEventPayload, getState, playerEvents]
  );

  const addListener = useCallback(
    (event: PlayerEvent, cb: PlayerEventCallback) => {
      playerEvents?.addListener(event, cb);

      if (event === "error" && getState().error) {
        playerEvents?.dispatch("error");
      }
    },
    [getState, playerEvents]
  );

  useEffect(() => {
    setPlayerEvents(new PlayerEvents());
  }, []);

  useEffect(() => {
    if (playerEvents) {
      setExternalEvents({
        addListener,
        dispatch: event => dispatch(event)(),
        removeListener: playerEvents.removeListener,
        removeListeners: playerEvents.removeListeners
      });
    }
  }, [addListener, dispatch, playerEvents, setExternalEvents]);

  useEffect(() => {
    const unsubs = [
      onError(dispatch("error")),
      onFinish(dispatch("onFinish")),
      onMediaChanged(dispatch("mediaChanged")),
      onPause(dispatch("pause")),
      onPipModeChange(dispatch("onPipModeChange")),
      onPlay(dispatch("play")),
      onProgress(dispatch("onProgress")),
      onRateChange(dispatch("ratechange")),
      onSeekEnd(dispatch("afterSeek")),
      onVolumeChange(dispatch("volumeChange")),
      onWaiting(dispatch("waiting"))
    ];

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [
    dispatch,
    onError,
    onFinish,
    onMediaChanged,
    onPause,
    onPlay,
    onPipModeChange,
    onProgress,
    onRateChange,
    onSeekEnd,
    onVolumeChange,
    onWaiting
  ]);
};
